import { ComponentClass, createElement, forwardRef, FunctionComponent, InputHTMLAttributes, NamedExoticComponent } from "react";
import classnames from "classnames";
import { RefType } from "../../../../types";
import { genericMemo } from "../../../../utils";
import { Tooltip } from "../../../ui";
import { FieldState, InputState } from "../../types";
import { InputProps } from "../Input";

import { ReactComponent as Success } from "./icons/checkmark-icon.svg";
import { ReactComponent as Progress } from "./icons/progress-icon.svg";

import styles from "./styles.module.scss";

export type InputPropsRequiredProps = Pick<InputProps, "hasError" | "errorText" | "endAdornment" | "className">

export type InputWithStateProps<
  P extends object = InputHTMLAttributes<HTMLInputElement>,
  R extends unknown = HTMLInputElement,
  TProps extends InputPropsRequiredProps = InputProps<P, R>
> = TProps & FieldState & {
  inputComponent: FunctionComponent<TProps> | ComponentClass<TProps, any> | NamedExoticComponent<TProps>
}

const InputComponent = <
  P extends object = InputHTMLAttributes<HTMLInputElement>,
  R extends unknown = HTMLInputElement,
  TProps extends InputPropsRequiredProps = InputProps<P, R>
>(props: InputWithStateProps<P, R, TProps>) => <></>;
export type InputComponentType = typeof InputComponent;

export const InputWithState = genericMemo(forwardRef(<
  P extends object = InputHTMLAttributes<HTMLInputElement>,
  R extends unknown = HTMLInputElement,
  TProps extends InputPropsRequiredProps = InputProps<P, R>
>(
  {
    inputComponent,
    state,
    stateText,
    ...props
  }: InputWithStateProps<P, R, TProps>,
  ref: RefType<R>,
) => createElement(
  inputComponent,
  {
    ...props as unknown as TProps,
    hasError: state === InputState.error || props.hasError,
    errorText: (state === InputState.error && stateText) || props.errorText,
    endAdornment: <>
      {props.endAdornment}
      {
        (state === InputState.progress && !props.hasError) &&
        <Tooltip content={stateText}>
          <Progress className={styles.progressIcon} />
        </Tooltip>
      }
      {
        (state === InputState.success && !props.hasError) &&
        <Tooltip content={stateText}>
          <Success className={styles.statusIcon} />
        </Tooltip>
      }
    </>,
    className: classnames(
      props.className,
      {
        [styles.progress]: state === InputState.progress && !props.hasError,
        [styles.success]: state === InputState.success && !props.hasError,
      },
    ),
    ref,
  },
)) as InputComponentType);

export default InputWithState;